import {
  alpha,
  Box,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { SyntheticEvent, useCallback } from 'react';

import { NotificationIcon } from '@frontend/components';
import { tApp } from '@frontend/locales';
import { usePortalConfiguration } from '@frontend/utils';

import { usePushNotifications } from './use-push-notifications';

export interface PushNotificationsToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  displayDescription?: boolean;
}

export const PushNotificationsToggle = ({
  checked,
  onChange,
  disabled,
  displayDescription,
}: PushNotificationsToggleProps) => {
  const { isSupported, canRequestPermission } = usePushNotifications();
  const {
    palette,
    shape: { borderRadius },
  } = useTheme();
  const {
    config: { operatorDisplayName },
  } = usePortalConfiguration();

  const handleChange = useCallback(
    (_e: SyntheticEvent, isChecked: boolean) => {
      onChange(isChecked);
    },
    [onChange],
  );

  if (!isSupported || !canRequestPermission) {
    return undefined;
  }

  const iconColor = checked ? palette.primary.main : palette.grey[500];

  return (
    <Box
      sx={{
        borderRadius: `${Number(borderRadius) * 3}px`,
        backgroundColor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'center',
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: `${Number(borderRadius) * 1.5}px`,
              backgroundColor: alpha(iconColor, 0.15),
              transition: 'background-color 0.2s',
            }}
          >
            <NotificationIcon
              stroke={iconColor}
              strokeOpacity={1}
              width={24}
              height={24}
            />
          </Box>

          <FormControlLabel
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              mx: 0,
            }}
            label={
              <Typography variant="body2" fontWeight={600}>
                {tApp.pushNotifications.switchLabel()}
              </Typography>
            }
            labelPlacement="start"
            control={
              <Switch
                size="small"
                sx={{
                  transform: 'scale(0.8)',
                  transformOrigin: 'center right',
                }}
              />
            }
            disabled={disabled}
            checked={checked}
            onChange={handleChange}
          />
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.75, display: 'block' }}
        >
          {tApp.pushNotifications.switchTagline({ operatorDisplayName })}
        </Typography>
      </Box>

      {displayDescription && (
        <FormHelperText sx={{ px: 2, pb: 1.5 }}>
          {tApp.pushNotifications.switchDescription({
            operatorDisplayName,
          })}
        </FormHelperText>
      )}
    </Box>
  );
};
